import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    TrendingUp,
    Calendar,
    DollarSign,
    Package,
    BarChart3,
    Bell,
    CreditCard,
    Shield,
    CheckCircle2,
    ArrowRight,
    Sparkles,
    Mail,
    Search,
    LayoutDashboard
} from 'lucide-react';
import SubscriptionLogo from '../../components/common/SubscriptionLogo';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer } from 'recharts';

//
// --- Placeholder Mock Components for the Hero ---
const MockSummaryCard = ({ title, value, subtext, icon: Icon, color, trend }) => (
    <div className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100 flex flex-col justify-between">
        <div className="flex items-start justify-between mb-3">
            <div className={`p-2 rounded-xl ${color}`}>
                <Icon className="w-5 h-5 text-white" />
            </div>
            {trend && (
                <span className={`text-xs font-medium px-2 py-1 rounded-full ${trend > 0 ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                    {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}%
                </span>
            )}
        </div>
        <div>
            <p className="text-gray-500 text-xs mb-1">{title}</p>
            <p className="text-xl font-bold text-gray-900">{value}</p>
            {subtext && <p className="text-[10px] text-gray-500 mt-1">{subtext}</p>}
        </div>
    </div>
);

const mockTrendData = [
    { month: "May '23", amount: 120 },
    { month: "Jun '23", amount: 110 },
    { month: "Jul '23", amount: 140 },
    { month: "Aug '23", amount: 130 },
    { month: "Sep '23", amount: 160 },
    { month: "Oct '23", amount: 150 },
];

const MockChartGraph = () => (
    <div className="w-full h-32 mt-4 relative">
        <ResponsiveContainer width="100%" height="100%">
            <LineChart
                data={mockTrendData}
                margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
            >
                <XAxis
                    dataKey="month"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: '#6B7280' }}
                    interval={0}
                />
                <YAxis hide />
                <Line
                    type="monotone"
                    dataKey="amount"
                    stroke="#3B82F6"
                    strokeWidth={2}
                    dot={{ fill: '#3B82F6', strokeWidth: 2, stroke: '#fff', r: 3 }}
                    activeDot={{ r: 5, stroke: '#3B82F6', strokeWidth: 2, fill: '#fff' }}
                />
            </LineChart>
        </ResponsiveContainer>
    </div>
);

const MockSubscriptionRow = ({ name, price, date }) => (
    <div className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-xl transition-colors border-b border-gray-50 last:border-0">
        <div className="flex items-center gap-3">
            <SubscriptionLogo name={name} className="w-10 h-10 rounded-xl text-lg" />
            <div>
                <p className="text-sm font-semibold text-gray-900">{name}</p>
                <p className="text-xs text-gray-500">Auto-renews {date}</p>
            </div>
        </div>
        <div className="text-right">
            <p className="text-sm font-bold text-gray-900">${price}</p>
            <p className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full inline-block mt-1">Active</p>
        </div>
    </div>
);

const LandingOptimized = () => {
    const [isHoveringMockup, setIsHoveringMockup] = useState(false);

    return (
        <div className="min-h-screen bg-gray-50 text-gray-900 font-sans selection:bg-indigo-500/30 overflow-x-hidden">

            {/* --- Navigation --- */}
            <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-md border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <img src="/logo.png" alt="Subby - AI subscription manager and subscription tracker logo" width="36" height="36" className="h-9 w-auto object-contain -mr-2" />
                        <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-700 tracking-tight">Subby</span>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-4">
                        <Link to="/login" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors hidden sm:block">
                            Log in
                        </Link>
                        <Link to="/signup" className="text-sm font-medium px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm transition-all hover:shadow-md active:scale-95">
                            Get Started Free
                        </Link>
                    </div>
                </div>
            </nav>

            {/* --- Hero Section --- */}
            <main className="pt-32 pb-20 px-6 relative overflow-hidden flex flex-col items-center">

                {/* Subtle background gradients */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-indigo-100/50 blur-[100px] rounded-full pointer-events-none -z-10" />

                <div className="max-w-4xl mx-auto text-center relative z-10 mb-16">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-indigo-50 border border-indigo-100 text-xs sm:text-sm text-indigo-600 font-semibold mb-8 shadow-sm">
                        <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse" />
                        The average person wastes $240/year on forgotten subscriptions
                    </div>

                    <h1 className="text-5xl md:text-7xl font-extrabold mb-6 tracking-tight text-gray-900 leading-[1.1]">
                        Find every subscription. <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-500">
                            Cancel what you don't need.
                        </span>
                    </h1>

                    <p className="text-lg md:text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed">
                        Connect your Gmail and Subby finds all your recurring charges in under 60 seconds. See exactly where your money goes — and stop paying for things you forgot about.
                    </p>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                        <Link to="/signup" className="w-full sm:w-auto px-8 py-4 rounded-xl bg-indigo-600 text-white font-semibold text-lg hover:bg-indigo-700 transition-all duration-300 hover:shadow-xl hover:shadow-indigo-500/20 active:scale-95 flex items-center justify-center gap-2 group">
                            Find My Hidden Subscriptions
                            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </Link>
                        <p className="text-sm text-gray-500 mt-3 sm:mt-0 sm:ml-4 flex items-center gap-1.5">
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                            No credit card required
                        </p>
                    </div>
                </div>

                {/* --- Dashboard UI Mockup --- */}
                <div
                    className="w-full max-w-5xl mx-auto relative z-20 transition-transform duration-700 ease-out perspective-1000"
                    onMouseEnter={() => setIsHoveringMockup(true)}
                    onMouseLeave={() => setIsHoveringMockup(false)}
                    style={{
                        transform: isHoveringMockup ? 'translateY(-10px) rotateX(2deg)' : 'translateY(0) rotateX(5deg) scale(0.98)',
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {/* Browser Chrome / Window Frame */}
                    <div className="bg-white rounded-t-2xl border border-gray-200 border-b-0 p-3 flex items-center gap-2 shadow-2xl relative z-10 overflow-hidden">
                        <div className="flex gap-1.5">
                            <div className="w-3 h-3 rounded-full bg-red-400" />
                            <div className="w-3 h-3 rounded-full bg-amber-400" />
                            <div className="w-3 h-3 rounded-full bg-green-400" />
                        </div>
                        <div className="mx-auto bg-gray-100 rounded-md px-4 py-1 flex items-center gap-2 text-xs text-gray-500 w-64 justify-center font-medium absolute left-1/2 -translate-x-1/2">
                            <Shield className="w-3 h-3" />
                            app.subby.io/dashboard
                        </div>
                    </div>

                    {/* Main Mockup Body */}
                    <div className="bg-gray-50 border border-gray-200 rounded-b-2xl shadow-2xl overflow-hidden flex flex-col ring-1 ring-gray-900/5">

                        {/* Mock Top Nav */}
                        <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between shrink-0 hidden sm:flex">
                            <div className="flex items-center gap-6">
                                <div className="text-xl font-black text-indigo-600 mr-4">Subby</div>
                                <div className="flex items-center gap-2">
                                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-indigo-50 text-indigo-600 text-sm font-medium">
                                        <BarChart3 className="w-4 h-4" />
                                        Home
                                    </div>
                                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-gray-500 text-sm font-medium">
                                        <CreditCard className="w-4 h-4" />
                                        Subscriptions
                                    </div>
                                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-gray-500 text-sm font-medium">
                                        <TrendingUp className="w-4 h-4" />
                                        Analytics
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center border border-indigo-200">
                                    <span className="text-sm font-bold text-indigo-700">JS</span>
                                </div>
                            </div>
                        </div>

                        {/* Mock Dashboard Content */}
                        <div className="flex-1 px-6 py-4 overflow-hidden bg-gray-50 h-[450px]">
                            <div className="flex justify-between items-center mb-6">
                                <div className="flex-1"></div>
                                <div className="bg-gray-100 p-1 rounded-xl flex items-center">
                                    <div className="bg-white px-4 py-1.5 rounded-lg text-xs font-medium text-gray-900 shadow-sm border border-gray-200">
                                        Monthly
                                    </div>
                                    <div className="px-4 py-1.5 rounded-lg text-xs font-medium text-gray-500">
                                        Yearly
                                    </div>
                                </div>
                            </div>

                            {/* KPI Cards Row */}
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                                <MockSummaryCard
                                    title="Total Monthly Spend"
                                    value="$142.94"
                                    icon={DollarSign}
                                    color="bg-green-500"
                                    trend={-5.2}
                                />
                                <MockSummaryCard
                                    title="Active Subscriptions"
                                    value="12"
                                    icon={Package}
                                    color="bg-indigo-500"
                                    trend={8.4}
                                />
                                <MockSummaryCard
                                    title="Upcoming Renewals"
                                    value="3"
                                    subtext="Next 7 days"
                                    icon={Calendar}
                                    color="bg-orange-500"
                                />
                                <MockSummaryCard
                                    title="Average Cost"
                                    value="$11.91"
                                    subtext="Per active sub"
                                    icon={TrendingUp}
                                    color="bg-purple-500"
                                />
                            </div>

                            {/* Main Content Area */}
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                {/* Big Chart Area */}
                                <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                                    <div className="flex items-center justify-between mb-3">
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900">Spending Trend</h3>
                                            <p className="text-sm text-gray-500">Last 6 months</p>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <TrendingUp className="w-5 h-5 text-red-500" />
                                            <span className="text-sm font-medium text-red-600">
                                                +5.2%
                                            </span>
                                        </div>
                                    </div>
                                    <MockChartGraph />
                                    <div className="pt-3 border-t border-gray-100 mt-2">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-gray-600">Current month</span>
                                            <span className="text-lg font-semibold text-gray-900">
                                                $150.00
                                            </span>
                                        </div>
                                        <div className="mt-1">
                                            <span className="text-xs text-gray-500">
                                                Increased from last month
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Upcoming List */}
                                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex flex-col">
                                    <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                        <Bell className="w-4 h-4 text-orange-500" /> Wait, you're paying for...
                                    </h3>
                                    <div className="flex-1 overflow-hidden relative">
                                        {/* Gradient fade out at bottom */}
                                        <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-white to-transparent z-10" />
                                        <MockSubscriptionRow name="Netflix Premium" price="22.99" date="Oct 14" />
                                        <MockSubscriptionRow name="Adobe CC" price="54.99" date="Oct 18" />
                                        <MockSubscriptionRow name="Spotify Duo" price="14.99" date="Oct 22" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Decorative shadow under the floating mockup */}
                    <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 w-[80%] h-10 bg-gray-900/10 blur-[30px] rounded-full -z-10" />
                </div>
            </main>

            {/* --- Logos / Social Proof --- */}
            <section className="py-10 border-t border-gray-200 bg-white">
                <div className="max-w-7xl mx-auto px-6 text-center">
                    <p className="text-sm font-semibold tracking-wider text-gray-400 uppercase mb-8">Securely tracks subscriptions from your favorite services</p>
                    <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-40 grayscale hover:grayscale-0 transition-all duration-500">
                        {['Netflix', 'Spotify', 'Adobe', 'Amazon', 'Apple', 'Google'].map(brand => (
                            <span key={brand} className="text-xl md:text-2xl font-black text-gray-400 tracking-tight">{brand}</span>
                        ))}
                    </div>
                </div>
            </section>

            {/* --- How It Works --- */}
            <section id="how-it-works" className="py-24 px-6 bg-white border-t border-gray-100">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-extrabold mb-4 text-gray-900">Up and running in <span className="text-indigo-600">60 seconds</span></h2>
                        <p className="text-gray-500 text-lg max-w-2xl mx-auto">No forms to fill out. No bank logins. Just connect your Gmail and Subby does the rest.</p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8 md:gap-12">
                        {/* Step 1 */}
                        <div className="text-center group">
                            <div className="w-16 h-16 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform shadow-sm">
                                <Mail className="w-8 h-8" />
                            </div>
                            <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-indigo-600 text-white text-sm font-bold mb-4">1</div>
                            <h3 className="text-xl font-bold mb-2 text-gray-900">Connect Your Gmail</h3>
                            <p className="text-gray-500 leading-relaxed">Secure read-only access. We never send, edit, or delete your emails.</p>
                        </div>

                        {/* Step 2 */}
                        <div className="text-center group">
                            <div className="w-16 h-16 rounded-2xl bg-green-50 text-green-600 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform shadow-sm">
                                <Search className="w-8 h-8" />
                            </div>
                            <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-indigo-600 text-white text-sm font-bold mb-4">2</div>
                            <h3 className="text-xl font-bold mb-2 text-gray-900">Subby Scans For You</h3>
                            <p className="text-gray-500 leading-relaxed">Our AI finds receipts, invoices, and renewal notices automatically.</p>
                        </div>

                        {/* Step 3 */}
                        <div className="text-center group">
                            <div className="w-16 h-16 rounded-2xl bg-orange-50 text-orange-500 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform shadow-sm">
                                <LayoutDashboard className="w-8 h-8" />
                            </div>
                            <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-indigo-600 text-white text-sm font-bold mb-4">3</div>
                            <h3 className="text-xl font-bold mb-2 text-gray-900">See The Full Picture</h3>
                            <p className="text-gray-500 leading-relaxed">Get your subscription dashboard with spending trends and cancellation guides.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* --- Value Prop / Features --- */}
            <section id="features" className="py-24 px-6 bg-gray-50 relative z-10">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-extrabold mb-4 text-gray-900">Know exactly where <span className="text-indigo-600">your money goes.</span></h2>
                        <p className="text-gray-500 text-lg max-w-2xl mx-auto">Tired of mystery charges on your bank statement? Subby connects to your inbox and reveals every recurring payment in under a minute.</p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="p-8 rounded-3xl bg-white shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 group hover:-translate-y-1">
                            <div className="w-14 h-14 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                <Sparkles className="w-7 h-7" />
                            </div>
                            <h3 className="text-2xl font-bold mb-3 text-gray-900">Find Subscriptions You Forgot About</h3>
                            <p className="text-gray-500 leading-relaxed">The average person has 12 active subscriptions but only remembers 8. Connect your Gmail and Subby finds the rest automatically.</p>
                        </div>

                        <div className="p-8 rounded-3xl bg-white shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 group hover:-translate-y-1">
                            <div className="w-14 h-14 rounded-2xl bg-green-50 text-green-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                <BarChart3 className="w-7 h-7" />
                            </div>
                            <h3 className="text-2xl font-bold mb-3 text-gray-900">See Where Every Dollar Goes</h3>
                            <p className="text-gray-500 leading-relaxed">Visual breakdowns by category, billing cycle, and trend. Know exactly where your money goes every month so you can cut what doesn't matter.</p>
                        </div>

                        <div className="p-8 rounded-3xl bg-white shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 group hover:-translate-y-1">
                            <div className="w-14 h-14 rounded-2xl bg-orange-50 text-orange-500 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                <Bell className="w-7 h-7" />
                            </div>
                            <h3 className="text-2xl font-bold mb-3 text-gray-900">Never Get Surprise-Charged Again</h3>
                            <p className="text-gray-500 leading-relaxed">Get notified 3 days before every renewal so you can cancel before you're charged. No more surprise bills draining your account.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* --- FAQ Section --- */}
            <section id="faq" className="py-24 px-6 bg-white border-t border-gray-100" aria-label="Subscription tracker and manager frequently asked questions">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-extrabold mb-4 text-gray-900">Frequently Asked Questions</h2>
                        <p className="text-gray-500 text-lg max-w-2xl mx-auto">Everything you need to know about managing your subscriptions with Subby.</p>
                    </div>
                    <div className="space-y-6">
                        <article className="p-6 bg-gray-50 rounded-2xl border border-gray-100">
                            <h3 className="text-lg font-bold text-gray-900 mb-2">How does Subby find my subscriptions?</h3>
                            <p className="text-gray-600">Subby securely connects to your Gmail account and uses intelligent algorithms to scan for receipts, invoices, and welcome emails from subscription services.</p>
                        </article>
                        <article className="p-6 bg-gray-50 rounded-2xl border border-gray-100">
                            <h3 className="text-lg font-bold text-gray-900 mb-2">Is my data secure?</h3>
                            <p className="text-gray-600">Your privacy comes first — always. You sign in with your existing Google account (no new passwords to remember), and we can only <strong>read</strong> subscription-related emails like receipts and invoices. We can never edit, send, or delete anything. All your data is fully end to end encrypted and never shared or sold. Users already trust Subby to keep their information safe.</p>
                        </article>
                        <article className="p-6 bg-gray-50 rounded-2xl border border-gray-100">
                            <h3 className="text-lg font-bold text-gray-900 mb-2">Can Subby cancel subscriptions for me?</h3>
                            <p className="text-gray-600">We provide step-by-step cancellation guides with direct links to help you cancel any subscription quickly and easily. We're also actively working on an AI-powered auto-cancel agent that will handle the entire cancellation process for you — stay tuned!</p>
                        </article>
                        <article className="p-6 bg-gray-50 rounded-2xl border border-gray-100">
                            <h3 className="text-lg font-bold text-gray-900 mb-2">Is there a free trial?</h3>
                            <p className="text-gray-600">Yes! You can connect your first Gmail account and uncover up to 5 subscriptions completely for free. Our Pro plan unlocks unlimited tracking, advanced insights, and multiple accounts.</p>
                        </article>
                        <article className="p-6 bg-gray-50 rounded-2xl border border-gray-100">
                            <h3 className="text-lg font-bold text-gray-900 mb-2">How much does Subby cost?</h3>
                            <p className="text-gray-600">Most people never need to pay a cent. Subby lets you <strong>scan, view, and analyse all your subscriptions for free</strong> — that includes renewal tracking, spending breakdowns, and step-by-step cancellation guides. For power users who want the full AI experience, our Pro plan is just <strong>$4.99/month</strong> — less than a single forgotten subscription costs — and adds smart insights like duplicate subscription detection across accounts, personalised savings recommendations, and predictive spending alerts. Most users save many times that in the first month alone.</p>
                        </article>
                        <article className="p-6 bg-gray-50 rounded-2xl border border-gray-100">
                            <h3 className="text-lg font-bold text-gray-900 mb-2">How do I find all my subscriptions?</h3>
                            <p className="text-gray-600">Connect your Gmail to Subby and it automatically scans your inbox for receipts, invoices, and renewal notices. Most people discover 3–5 forgotten subscriptions they're still paying for. The entire scan takes under 60 seconds and Subby never sends, edits, or deletes your emails.</p>
                        </article>
                        <article className="p-6 bg-gray-50 rounded-2xl border border-gray-100">
                            <h3 className="text-lg font-bold text-gray-900 mb-2">Is Subby a subscription tracker or subscription manager app?</h3>
                            <p className="text-gray-600">Subby is both a subscription tracker and a subscription manager app. It automatically finds and tracks all your recurring payments via Gmail, shows you spending breakdowns, sends renewal alerts, and provides step-by-step cancellation guides — all for free. Think of it as the simplest app that tracks subscriptions, manages them, and helps you cancel the ones you don't need.</p>
                        </article>
                        <article className="p-6 bg-gray-50 rounded-2xl border border-gray-100">
                            <h3 className="text-lg font-bold text-gray-900 mb-2">How do I cancel subscriptions I don't use anymore?</h3>
                            <p className="text-gray-600">Subby makes cancelling unused subscriptions easy. After scanning your Gmail, Subby shows you all your active subscriptions with step-by-step cancellation guides and direct links to each service's cancellation page. You can also get renewal alerts 3 days before every charge, so you can cancel before you're billed again.</p>
                        </article>
                    </div>
                </div>
            </section>

            {/* --- Bottom CTA --- */}
            <section className="py-20 bg-indigo-600 text-white text-center px-6">
                <div className="max-w-3xl mx-auto">
                    <h2 className="text-3xl md:text-5xl font-bold mb-6 text-white">Ready to see where your money really goes?</h2>
                    <p className="text-indigo-100 mb-4 text-lg">Connect your Gmail and uncover every forgotten subscription in under 60 seconds.</p>
                    <p className="text-indigo-200 mb-10 text-sm">Free forever for most users. No credit card. Cancel anytime.</p>
                    <Link to="/signup" className="inline-flex items-center justify-center px-8 py-4 rounded-xl bg-white text-indigo-600 font-bold text-lg hover:bg-gray-50 transition-colors shadow-lg hover:shadow-xl active:scale-95 group">
                        Find My Hidden Subscriptions
                        <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Link>
                </div>
            </section>

            {/* --- Footer --- */}
            <footer className="py-12 px-6 border-t border-gray-200 bg-white">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="flex items-center gap-2">
                        <img src="/logo.png" alt="Subby - AI subscription manager and subscription tracker logo" width="28" height="28" className="h-7 w-auto object-contain -mr-1" />
                        <span className="font-bold text-gray-900 tracking-tight">Subby</span>
                        <span className="text-gray-400 text-sm ml-2">© {new Date().getFullYear()} Subby. All rights reserved.</span>
                    </div>

                    <div className="flex flex-wrap justify-center gap-8 text-sm font-medium text-gray-500">
                        <Link to="/terms" className="hover:text-indigo-600 transition-colors">Terms of Use</Link>
                        <Link to="/privacy" className="hover:text-indigo-600 transition-colors">Privacy Policy</Link>
                        <a href="mailto:support@subby.io" className="hover:text-indigo-600 transition-colors">support@subby.io</a>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LandingOptimized;
